/**
 * Comprehensive unit system for SixSight Cricket Analytics
 * Supports multiple unit systems with automatic conversion
 */

export type SpeedUnit = 'mps' | 'kmh' | 'mph';
export type DistanceUnit = 'meters' | 'feet' | 'yards';
export type AngleUnit = 'degrees' | 'radians';
export type MassUnit = 'kg' | 'lbs' | 'oz';
export type TemperatureUnit = 'celsius' | 'fahrenheit' | 'kelvin';

export interface UnitSystemConfig {
  speed: SpeedUnit;
  distance: DistanceUnit;
  angle: AngleUnit;
  mass: MassUnit;
  temperature: TemperatureUnit;
}

export interface UnitPreferences extends UnitSystemConfig {
  decimalPlaces: {
    speed: number;
    distance: number;
    angle: number;
    mass: number;
    temperature: number;
  };
}

export interface UnitDefinition {
  symbol: string;
  name: string;
  shortName: string;
  pluralName?: string;
}

export const UNIT_DEFINITIONS: Record<string, UnitDefinition> = {
  // Speed
  mps: { symbol: 'm/s', name: 'meters per second', shortName: 'm/s' },
  kmh: { symbol: 'km/h', name: 'kilometers per hour', shortName: 'km/h' },
  mph: { symbol: 'mph', name: 'miles per hour', shortName: 'mph' },
  
  // Distance
  meters: { symbol: 'm', name: 'meters', shortName: 'm', pluralName: 'meters' },
  feet: { symbol: 'ft', name: 'feet', shortName: 'ft', pluralName: 'feet' },
  yards: { symbol: 'yd', name: 'yards', shortName: 'yd', pluralName: 'yards' },
  
  // Angle
  degrees: { symbol: '°', name: 'degrees', shortName: '°', pluralName: 'degrees' },
  radians: { symbol: 'rad', name: 'radians', shortName: 'rad', pluralName: 'radians' },
  
  // Mass
  kg: { symbol: 'kg', name: 'kilograms', shortName: 'kg', pluralName: 'kilograms' },
  lbs: { symbol: 'lbs', name: 'pounds', shortName: 'lbs', pluralName: 'pounds' },
  oz: { symbol: 'oz', name: 'ounces', shortName: 'oz', pluralName: 'ounces' },
  
  // Temperature
  celsius: { symbol: '°C', name: 'degrees Celsius', shortName: '°C' },
  fahrenheit: { symbol: '°F', name: 'degrees Fahrenheit', shortName: '°F' },
  kelvin: { symbol: 'K', name: 'Kelvin', shortName: 'K' },
};

// Pre-defined unit systems for different regions
export const UNIT_SYSTEMS: Record<string, UnitSystemConfig> = {
  metric: {
    speed: 'kmh',
    distance: 'meters',
    angle: 'degrees',
    mass: 'kg',
    temperature: 'celsius'
  },
  imperial: {
    speed: 'mph',
    distance: 'feet',
    angle: 'degrees',
    mass: 'lbs',
    temperature: 'fahrenheit'
  },
  scientific: {
    speed: 'mps',
    distance: 'meters',
    angle: 'radians',
    mass: 'kg',
    temperature: 'kelvin'
  },
  cricket_traditional: {
    speed: 'mph', // Traditional cricket uses mph for ball speed
    distance: 'yards', // Cricket field dimensions traditionally in yards
    angle: 'degrees',
    mass: 'kg',
    temperature: 'celsius'
  }
};

// Default preferences
export const DEFAULT_UNIT_PREFERENCES: UnitPreferences = {
  ...UNIT_SYSTEMS.metric,
  decimalPlaces: {
    speed: 1,
    distance: 1,
    angle: 1,
    mass: 2,
    temperature: 1
  }
};

// Cricket-specific display contexts
export interface CricketMetricContext {
  type: 'ball_speed' | 'bat_speed' | 'exit_velocity' | 'distance' | 'height' | 
        'launch_angle' | 'horizontal_angle' | 'bat_mass' | 'ball_mass' | 
        'temperature' | 'spin_rate';
  importance: 'primary' | 'secondary' | 'detail';
  precision: 'low' | 'medium' | 'high';
}

export const CRICKET_METRIC_CONTEXTS: Record<string, CricketMetricContext> = {
  ballSpeed: { type: 'ball_speed', importance: 'primary', precision: 'medium' },
  batSpeed: { type: 'bat_speed', importance: 'primary', precision: 'medium' },
  exitVelocity: { type: 'exit_velocity', importance: 'primary', precision: 'medium' },
  distance: { type: 'distance', importance: 'primary', precision: 'high' },
  height: { type: 'height', importance: 'secondary', precision: 'medium' },
  launchAngle: { type: 'launch_angle', importance: 'primary', precision: 'high' },
  horizontalAngle: { type: 'horizontal_angle', importance: 'secondary', precision: 'medium' }
};